import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Stack} from '@mui/material';
import {
  Button,
  Headline,
  Icon,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import useNavigation from '@/hooks/use-navigation';
import contentStudioStore, {
  SelectedAsset,
  contentStudioActions,
} from '@/stores/contentStudioStore';
import ThumbnailDisplay from './ThumbnailDisplay';
import {shortenFileName} from './utils';

type UploadDisplayProps = {
  analyticsLocation: string;
  open: () => void;
  maxWidth: number;
  uploadInteractionId: string;
};

function UploadDisplay({
  analyticsLocation,
  open,
  maxWidth,
  uploadInteractionId,
}: UploadDisplayProps) {
  const {navigate} = useNavigation();
  const {uiInteraction} = useUiInteractionEvent();
  const {selectedAssets, files} = contentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
      files: state.files,
    }))
  );

  const getFilesMessage = () => {
    const uploadingFiles = selectedAssets.filter((asset) => asset.isUploading);

    const addedMessage =
      selectedAssets.length > 0
        ? translate('added_files', {
            files: selectedAssets.length - uploadingFiles.length,
            count: selectedAssets.length - uploadingFiles.length,
          })
        : '';

    const uploadingMessage =
      uploadingFiles.length > 0
        ? translate('uploading_files', {
            files: uploadingFiles.length,
            count: uploadingFiles.length,
          })
        : '';

    return [addedMessage, uploadingMessage].filter(Boolean).join(', ');
  };

  const displayAsset = (asset: SelectedAsset) => {
    const removeAsset = () => {
      const assets = selectedAssets.filter((a) => a.id !== asset.id);
      contentStudioActions.setSelectedAssets(assets);
    };
    if (asset.isUploading) {
      return (
        <ThumbnailDisplay
          onClose={removeAsset}
          file={Object.values(files).find((f) => f.id === asset.id)}
          isLoading={asset.isUploading}
        />
      );
    }
    return (
      <ThumbnailDisplay thumbnail={asset.thumbnail} onClose={removeAsset} />
    );
  };
  return (
    <Stack
      margin="0"
      direction="column"
      width="100%"
      maxWidth="1116px"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      justifyContent="space-between"
      overflow="hidden"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        width={maxWidth - 24}
        height="56px"
        justifySelf="flex-start"
        alignItems="center"
        paddingX="12px"
      >
        <Headline size="xxs" color={designSystemToken('semantic.fg.primary')}>
          {getFilesMessage()}
        </Headline>
        <Button
          appearance="neutral"
          mode="plain"
          size="large"
          onClick={() => {
            uiInteraction({
              item_name: 'clear_all',
              interaction_location: analyticsLocation,
            });
            contentStudioActions.setSelectedAssets([]);
          }}
        >
          <Label size="lg" color={designSystemToken('semantic.fg.secondary')}>
            {translate('Clear all')}
          </Label>
        </Button>
      </Stack>
      <Stack
        direction="row"
        gap="24px"
        width="100%"
        maxWidth={maxWidth - 24}
        paddingX="12px"
        sx={{
          'overflowX': 'auto',
          'paddingY': '12px',
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: designSystemToken('semantic.bg.alpha'),
            marginLeft: '12px',
            marginRight: '12px',
          },
          '&::-webkit-scrollbar-thumb': {
            'background': designSystemToken('semantic.fg.tertiary'),
            'borderRadius': '99px',
            '&:hover': {
              background: designSystemToken('semantic.bg.alpha-secondary'),
            },
          },
        }}
      >
        {selectedAssets.map((asset) => (
          <Stack
            key={asset.id}
            direction="column"
            gap="8px"
            alignItems="center"
          >
            {displayAsset(asset)}
            <Headline size="xxs">
              {shortenFileName(asset.fileName || '')}
            </Headline>
          </Stack>
        ))}
      </Stack>
      <Stack
        direction="row"
        gap="8px"
        paddingTop="16px"
        paddingBottom="12px"
        paddingX="12px"
      >
        <Stack direction="row" gap="8px">
          <Button
            appearance="neutral"
            mode="tinted"
            size="large"
            sx={{
              paddingLeft: '16px',
              paddingRight: '20px',
            }}
            startIcon={
              <Icon name="Actions-Upload" size="medium" appearance="primary" />
            }
            onClick={() => {
              uiInteraction({
                item_name: 'add_from_computer',
                interaction_location: analyticsLocation,
                interaction_id: uploadInteractionId,
              });
              open();
            }}
          >
            <Label size="lg" color={designSystemToken('semantic.fg.neutral')}>
              {translate('Add from Computer')}
            </Label>
          </Button>
        </Stack>
        <Button
          appearance="neutral"
          mode="filled"
          size="large"
          style={{marginLeft: 'auto'}}
          disabled={selectedAssets.some((a) => a.isUploading)}
          onClick={() => {
            uiInteraction({
              item_name: 'go_to_editor',
              interaction_location: analyticsLocation,
            });
            navigate(ROUTES.CONTENT_LAB_FEATURE);
          }}
        >
          {translate('Go to Editor')}
        </Button>
      </Stack>
    </Stack>
  );
}

export default UploadDisplay;
